"use client";
import React, { useState } from "react";
import Image from "next/image";
import Icon from "@/components/icons/Icons";
import SectionHeading from "../others/SectionHeading";
import { GroupedWorstReturns, RecoveryGrade } from "@/type/comparePageTypes";
import { useLoginModal } from "@/utils/useLoginModal";
import { buildWorstYearDefenseScores } from "@/utils/comparePageFunctions";
import { QuarterlyReturnRow } from "@/type/commonTypes";
import { OverlapSchemes } from "@/type/overlapType";
import { AnimatePresence, easeInOut, motion } from "framer-motion";
import { useIsMobile } from "@/utils/useIsMobile";

type AccordionKey = "defense" | "worst" | "recovery";

type RecoveryColor = {
  text: string;
  bg: string;
  border: string;
};

type Props = {
  worstReturns: QuarterlyReturnRow[];
  funds: OverlapSchemes[];
  otherFunds: GroupedWorstReturns;
};

const RECOVERY_COLOR_MAP: Record<RecoveryGrade, RecoveryColor> = {
  excellent: {
    text: "text-[#00D56A]",
    bg: "bg-[#00BB5E1A]",
    border: "border-[#00BB5E33]",
  },
  fast: {
    text: "text-[#51A2FF]",
    bg: "bg-[#2B7FFF1A]",
    border: "border-[#2B7FFF33]",
  },
  slow: {
    text: "text-[#FFA406]",
    bg: "bg-[#FFD0791A]",
    border: "border-[#FFD07933]",
  },
};

function getRecoveryColors(recovery: RecoveryGrade): RecoveryColor {
  return RECOVERY_COLOR_MAP[recovery];
}

export default function AdvanceInsightSection({ worstReturns, funds, otherFunds }: Props) {
  const { isLoggedIn, open, setModalKey } = useLoginModal();
  const [openKey, setOpenKey] = useState<AccordionKey | null>("defense");
  const scoredFunds = buildWorstYearDefenseScores({ worstReturns, funds, otherFunds, isLoggedIn });

  const isMob = useIsMobile(1025);

  const toggle = (key: AccordionKey) => {
    if (!isMob) return;
    setOpenKey((prev) => (prev === key ? null : key));
  };

  const accordionMotion = isMob
    ? {
        initial: { height: 0, opacity: 0 },
        animate: { height: "auto", opacity: 1 },
        exit: { height: 0, opacity: 0 },
        transition: {
          duration: 0.35,
          ease: easeInOut,
        },
      }
    : {};

  return (
    <div className="py-16">
      <SectionHeading heading="Advanced Investor Insights" subHeading="Go deeper than returns, see how these funds hold up in market crashes, downturns and challenging economic times." className="mb-7" dark />

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 relative ${!isLoggedIn ? "lg:h-131" : ""}`}>
        {/* Defense Card */}
        <div className={`bg-[#1d313e] border rounded-4xl p-4 sm:p-6 ${openKey === "defense" ? "border-white shadow-[0px_0px_26px_0px_#EE9300]" : "border-white xs:border-white xs:shadow-[0px_0px_26px_0px_#EE9300]"}`}>
          <div onClick={() => toggle("defense")} className="flex items-center gap-2 w-full h-15 text-left cursor-pointer lg:cursor-default">
            {/* Icon */}
            <Image src="/next_assets/images/compare-pages/defense-icon.png" alt="insight icons" width={48} height={48} className="w-10 h-10 sm:w-12 sm:h-12" />

            <div className="text-white flex flex-col gap-1">
              <span className="font-bold xs:text-xl xs:leading-1.2">Defense Score</span>
              <span className="opacity-70 font-light tracking-wider text-xs leading-1.3">Ability to resist market falls</span>
            </div>

            <Icon.dropdownDownArrow className={`lg:hidden text-white shrink-0 transition duration-300 ease-linear ml-auto ${openKey === "defense" ? "rotate-180" : ""}`} />
          </div>

          <AnimatePresence initial={false}>
            {openKey === "defense" && (
              <motion.div {...accordionMotion} className="lg:hidden overflow-hidden">
                {scoredFunds.map((fund, index) => {
                  const score = fund.defenseScore > 0 ? fund.defenseScore : 3;
                  let message = "Moderate protection against market fluctuations.";

                  if (index === 0) {
                    message = "Outstanding protection during market downturns.";
                  } else if (index === scoredFunds.length - 1) {
                    message = "More vulnerable during market declines.";
                  } else {
                    message = "Strong ability to limit losses in volatile markets.";
                  }
                  return (
                    <div key={fund.isin} className="bg-[#1D293DCC] border border-[#314158] rounded-xl px-3 py-2.5 space-y-2.5 mt-4">
                      <div className="flex items-center justify-between gap-4 text-white">
                        <span className="text-sm font-medium text-[#CAD5E2] line-clamp-1">{fund.fund.scheme_name}</span>
                        <span className="text-sm text-[#62748E] shrink-0">
                          <span className="text-lg font-bold leading-1.2 text-white">{score}</span> /100
                        </span>
                      </div>

                      <div className="h-2 w-full rounded-full bg-[#31415880] overflow-hidden">
                        <div className="h-full bg-[#00C950] rounded-full" style={{ width: `${score}%` }} />
                      </div>

                      <div className="text-[#798eac] text-xs leading-1.3">{message}</div>
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="hidden lg:block">
            {scoredFunds.map((fund, index) => {
              const score = fund.defenseScore > 0 ? fund.defenseScore : 3;
              let message = "Moderate protection against market fluctuations.";

              if (index === 0) {
                message = "Outstanding protection during market downturns.";
              } else if (index === scoredFunds.length - 1) {
                message = "More vulnerable during market declines.";
              } else {
                message = "Strong ability to limit losses in volatile markets.";
              }
              return (
                <div key={fund.isin} className="bg-[#1D293DCC] border border-[#314158] rounded-xl px-3 py-2.5 space-y-2.5 mt-4">
                  <div className="flex items-center justify-between gap-4 text-white">
                    <span className="text-sm font-medium text-[#CAD5E2] line-clamp-1">{fund.fund.scheme_name}</span>
                    <span className="text-sm text-[#62748E] shrink-0">
                      <span className="text-lg font-bold leading-1.2 text-white">{score}</span> /100
                    </span>
                  </div>

                  <div className="h-2 w-full rounded-full bg-[#31415880] overflow-hidden">
                    <div className="h-full bg-[#00C950] rounded-full" style={{ width: `${score}%` }} />
                  </div>

                  <div className="text-[#798eac] text-xs leading-1.3">{message}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Worst Case Card */}
        <div className={`bg-[#1d313e] border rounded-4xl p-4 sm:p-6 flex flex-col gap-4 ${openKey === "worst" ? "border-white shadow-[0px_0px_26px_0px_#EE9300]" : "border-white xs:border-white xs:shadow-[0px_0px_26px_0px_#EE9300]"}`}>
          <div onClick={() => toggle("worst")} className="flex items-center gap-2 w-full h-15 text-left cursor-pointer lg:cursor-default">
            {/* Icon */}
            <Image src="/next_assets/images/compare-pages/worst-case-icon.png" alt="insight icons" width={48} height={48} className="w-10 h-10 sm:w-12 sm:h-12" />

            <div className="text-white flex flex-col gap-1">
              <span className="font-bold xs:text-xl xs:leading-1.2">Worst Case</span>
              <span className="opacity-70 tracking-wider text-xs leading-1.3">Max loss in a single year (Since Inception)</span>
            </div>

            <Icon.dropdownDownArrow className={`lg:hidden text-white shrink-0 transition duration-300 ease-linear ml-auto ${openKey === "worst" ? "rotate-180" : ""}`} />
          </div>

          <AnimatePresence initial={false}>
            {openKey === "worst" && (
              <motion.div {...accordionMotion} className="lg:hidden overflow-hidden">
                {scoredFunds.map((fund) => (
                  <div key={fund.isin} className="flex items-center gap-3 justify-between bg-[#1D293DCC] border border-[#314158] rounded-xl px-3 h-14 mb-3.5">
                    <span className="text-sm font-medium text-[#CAD5E2] line-clamp-1">{fund.fund.scheme_name}</span>
                    <span className={`text-lg font-bold leading-1.2 ${fund.worstReturn > 0 ? "text-[#00D56A]" : "text-[#FF6467]"}`}>{String(fund.worstReturn.toFixed(1))}%</span>
                  </div>
                ))}

                <div className="border-t border-[#31415880] pt-4.5 mt-auto">
                  <div className="text-[#FFA2A2CC] text-xs p-3 bg-[#FB2C3614] border border-[#FB2C3626] rounded-xl">
                    <strong className="text-[#FF6467]">Note:</strong> This shows the biggest drop the fund has ever seen in a calendar year. High returns often come with bigger drops.
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="hidden h-full lg:flex flex-col gap-4.5">
            {scoredFunds.map((fund) => (
              <div key={fund.isin} className="flex items-center gap-3 justify-between bg-[#1D293DCC] border border-[#314158] rounded-xl px-3 h-14">
                <span className="text-sm font-medium text-[#CAD5E2] line-clamp-1">{fund.fund.scheme_name}</span>
                <span className={`text-lg font-bold leading-1.2 ${fund.worstReturn > 0 ? "text-[#00D56A]" : "text-[#FF6467]"}`}>{String(fund.worstReturn.toFixed(1))}%</span>
              </div>
            ))}

            <div className="border-t border-[#31415880] pt-4.5 mt-auto">
              <div className="text-[#FFA2A2CC] text-xs p-3 bg-[#FB2C3614] border border-[#FB2C3626] rounded-xl">
                <strong className="text-[#FF6467]">Note:</strong> This shows the biggest drop the fund has ever seen in a calendar year. High returns often come with bigger drops.
              </div>
            </div>
          </div>
        </div>

        {/* Recovery Card */}
        <div className={`bg-[#1d313e] border rounded-4xl p-4 sm:p-6 flex flex-col gap-4 ${openKey === "recovery" ? "border-white shadow-[0px_0px_26px_0px_#EE9300]" : "border-white xs:border-white xs:shadow-[0px_0px_26px_0px_#EE9300]"}`}>
          <div onClick={() => toggle("recovery")} className="flex items-center gap-2 w-full h-15 text-left cursor-pointer lg:cursor-default">
            {/* Icon */}
            <Image src="/next_assets/images/compare-pages/recovery-icon.png" alt="insight icons" width={48} height={48} className="w-10 h-10 sm:w-12 sm:h-12" />

            <div className="text-white flex flex-col gap-1">
              <span className="font-bold xs:text-xl xs:leading-1.2">Recovery Speed</span>
              <span className="opacity-70 tracking-wider text-xs leading-1.3">How fast it bounces back</span>
            </div>

            <Icon.dropdownDownArrow className={`lg:hidden text-white shrink-0 transition duration-300 ease-linear ml-auto ${openKey === "recovery" ? "rotate-180" : ""}`} />
          </div>

          <AnimatePresence initial={false}>
            {openKey === "recovery" && (
              <motion.div {...accordionMotion} className="lg:hidden overflow-hidden">
                {scoredFunds.map((fund) => {
                  const { text, bg, border } = getRecoveryColors(fund.recovery);

                  return (
                    <div key={fund.isin} className="flex items-center justify-between gap-3 bg-[#1D293DCC] border border-[#314158] rounded-xl px-3 h-14 mb-3.5">
                      <span className="text-sm font-medium text-[#CAD5E2] line-clamp-1">{fund.fund.scheme_name}</span>
                      <span className={`inline-block px-3.5 py-1 text-xs font-bold tracking-wide leading-1.2 ${text} ${bg} border ${border} rounded-full capitalize`}>{fund.recovery}</span>
                    </div>
                  );
                })}

                <div className="border-t border-[#31415880] pt-4.5 mt-auto">
                  <div className="text-[#C3EBD7] text-xs p-3 bg-[#00D56B0D] border border-[#00D56B1A] rounded-xl">
                    <strong className="text-[#00D56A]">Pro Tip:</strong> Funds with &quot;Excellent&quot; recovery are better for short-term goals, as you&apos;re less likely to be stuck in a loss when you need money.
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="hidden h-full lg:flex flex-col gap-4.5">
            {scoredFunds.map((fund) => {
              const { text, bg, border } = getRecoveryColors(fund.recovery);

              return (
                <div key={fund.isin} className="flex items-center justify-between gap-3 bg-[#1D293DCC] border border-[#314158] rounded-xl px-3 h-14">
                  <span className="text-sm font-medium text-[#CAD5E2] line-clamp-1">{fund.fund.scheme_name}</span>
                  <span className={`inline-block px-3.5 py-1 text-xs font-bold tracking-wide leading-1.2 ${text} ${bg} border ${border} rounded-full capitalize`}>{fund.recovery}</span>
                </div>
              );
            })}

            <div className="border-t border-[#31415880] pt-4.5 mt-auto">
              <div className="text-[#C3EBD7] text-xs p-3 bg-[#00D56B0D] border border-[#00D56B1A] rounded-xl">
                <strong className="text-[#00D56A]">Pro Tip:</strong> Funds with &quot;Excellent&quot; recovery are better for short-term goals, as you&apos;re less likely to be stuck in a loss when you need money.
              </div>
            </div>
          </div>
        </div>

        {!isLoggedIn && (
          <div className="absolute inset-0 sm:rounded-4xl sm:border border-white/50 bg-black/65 backdrop-blur-md flex flex-col items-center justify-center">
            <Image src="/next_assets/images/common-images/lock-img-v2.png" alt="Lock Image" width={200} height={200} className="w-24 h-24 sm:w-28 sm:h-w-28 object-contain mb-2" />

            <div className="space-y-3 text-center max-w-2xl px-2 mb-8">
              <div className="text-white text-xl sm:text-2xl md:text-3xl font-bold">Unlock Risk Analysis</div>
              <p className="text-[#CAD5E2] text-sm sm:text-base md:text-lg">See the most challenging moments of the mutual funds during major market crashes to do an informed risk assessment for your investments.</p>
            </div>

            <button
              onClick={() => {
                open();
                setModalKey("compare-page-insights");
              }}
              className="py-2.5 sm:py-3.5 w-full max-w-64 rounded-xl text-white border border-white/40 text-sm sm:text-base md:text-lg font-semibold sm:font-bold bg-[#BF7900] shine-button-hover"
            >
              Reveal Stress Test
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
